import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, apiError, tl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AuthModal } from "@/components/AuthModal";
import { BoostModal } from "@/components/BoostModal";
import { BoostCountdown } from "@/components/BoostCountdown";
import { ContactUnlock } from "@/components/ContactUnlock";
import { STATUS_LABELS, timeAgo } from "@/lib/categories";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  MapPin, Ruler, Package, Clock, Loader2, Star, Send,
  ShieldCheck, CheckCircle2, Heart, Pencil, Trash2, Rocket,
} from "lucide-react";
import { toast } from "sonner";

export default function JobDetail() {
  const { id } = useParams();
  const { user, config, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [offers, setOffers] = useState([]);
  const [authOpen, setAuthOpen] = useState(false);
  const [offerForm, setOfferForm] = useState({ price: "", duration_days: "", description: "", materials_included: false, start_date: "" });
  const [submitting, setSubmitting] = useState(false);
  const [contact, setContact] = useState(null);
  const [unlockOpen, setUnlockOpen] = useState(false);
  const [wallet, setWallet] = useState(null);
  const [unlocking, setUnlocking] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [boostOpen, setBoostOpen] = useState(false);
  const [editForm, setEditForm] = useState({ title: "", description: "", budget_min: "", budget_max: "", area_m2: "" });

  const isOwner = user && job && user.id === job.employer_id;
  const isUsta = user && user.active_mode === "usta";

  const load = async () => {
    const { data } = await api.get(`/jobs/${id}`);
    setJob(data);
  };
  useEffect(() => { load(); }, [id]);
  useEffect(() => {
    if (user && job) {
      if (isOwner) api.get(`/jobs/${id}/offers`).then(({ data }) => setOffers(data)).catch(() => {});
      api.get("/wallet").then(({ data }) => setWallet(data)).catch(() => {});
      api.get(`/jobs/${id}/contact`).then(({ data }) => setContact(data)).catch(() => {});
    }
  }, [user, job]);

  const submitOffer = async () => {
    if (!user || !user.id) { setAuthOpen(true); return; }
    if (!user.phone_verified) { toast.error("Teklif vermek için telefon numaranızı doğrulayın."); navigate("/hesabim?tab=phone"); return; }
    setSubmitting(true);
    try {
      const raw = Number(offerForm.price);
      const isOver = raw > 1000000;
      await api.post("/offers", {
        job_id: id, price: isOver ? 1000000 : raw, duration_days: Number(offerForm.duration_days) || 1,
        description: offerForm.description, materials_included: offerForm.materials_included, start_date: offerForm.start_date, is_over: isOver,
      });
      toast.success("Teklifiniz gönderildi!");
      setOfferForm({ price: "", duration_days: "", description: "", materials_included: false, start_date: "" });
    } catch (e) { toast.error(apiError(e)); }
    finally { setSubmitting(false); }
  };

  const doUnlock = async () => {
    setUnlocking(true);
    try {
      const { data } = await api.post(`/jobs/${id}/unlock-contact`);
      toast.success(data.already_unlocked ? "İletişim zaten açık" : "İletişim bilgisi açıldı!");
      setUnlockOpen(false);
      const { data: c } = await api.get(`/jobs/${id}/contact`);
      setContact(c);
      const { data: w } = await api.get("/wallet");
      setWallet(w);
    } catch (e) { toast.error(apiError(e)); }
    finally { setUnlocking(false); }
  };

  const startChat = () => {
    if (!user || !user.id) { setAuthOpen(true); return; }
    navigate(`/mesajlar?job=${id}&to=${job.employer_id}`);
  };

  const acceptOffer = async (offerId) => {
    try { await api.patch(`/offers/${offerId}/status?status=accepted`); toast.success("Teklif kabul edildi"); load(); api.get(`/jobs/${id}/offers`).then(({ data }) => setOffers(data)); }
    catch (e) { toast.error(apiError(e)); }
  };
  const rejectOffer = async (offerId) => {
    try { await api.patch(`/offers/${offerId}/status?status=rejected`); toast.success("Teklif reddedildi"); api.get(`/jobs/${id}/offers`).then(({ data }) => setOffers(data)); }
    catch (e) { toast.error(apiError(e)); }
  };

  const completeJob = async () => {
    try { await api.patch(`/jobs/${id}/status?status=completed`); toast.success("İş tamamlandı olarak işaretlendi"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const openEdit = () => {
    setEditForm({
      title: job.title, description: job.description,
      budget_min: job.budget_min || "", budget_max: job.budget_max || "", area_m2: job.area_m2 || "",
    });
    setEditOpen(true);
  };
  const saveEdit = async () => {
    try {
      await api.put(`/jobs/${id}`, {
        title: editForm.title, description: editForm.description, area_m2: editForm.area_m2,
        budget_min: editForm.budget_min ? Number(editForm.budget_min) : null,
        budget_max: editForm.budget_max ? Number(editForm.budget_max) : null,
      });
      toast.success("İlan güncellendi"); setEditOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const removeJob = async () => {
    if (!window.confirm("Bu ilanı yayından kaldırmak istediğinize emin misiniz? İlan tüm listelerden anında kaldırılacaktır.")) return;
    try { await api.patch(`/jobs/${id}/status?status=cancelled`); toast.success("İlan yayından kaldırıldı"); navigate("/hesabim?tab=jobs"); }
    catch (e) { toast.error(apiError(e)); }
  };

  const adminCancel = async () => {
    const reason = window.prompt("İlan iptal gerekçesi (Destek Talep No / Açıklama):", "");
    if (!reason || !reason.trim()) { toast.error("Gerekçe zorunludur"); return; }
    try { await api.post(`/admin/jobs/${id}/cancel`, { reason }); toast.success("İlan yönetici tarafından kaldırıldı"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (!job) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-emerald-500" /></div>;

  const price = config.contact_unlock_price;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 grid lg:grid-cols-[1fr_360px] gap-8">
      {/* Left */}
      <div className="space-y-6">
        <div className="bg-white rounded-2xl border border-slate-200/80 overflow-hidden">
          {job.photos?.[0] && <img src={job.photos[0]} alt={job.title} className="w-full h-64 object-cover" />}
          <div className="p-6">
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-full">{job.category_name}</span>
              <span className="text-xs font-semibold text-slate-600 bg-slate-100 px-2.5 py-1 rounded-full">{STATUS_LABELS[job.status]}</span>
              {job.boost_expires_at && new Date(job.boost_expires_at) > new Date() && <span className="inline-flex items-center gap-1 text-xs font-bold text-white brand-gradient px-2.5 py-1 rounded-full" data-testid="job-boosted-badge"><Rocket className="h-3 w-3" /> Öne Çıkan</span>}
            </div>
            <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 mt-3">{job.title}</h1>
            <div className="flex flex-wrap gap-x-4 gap-y-2 mt-3 text-sm text-slate-500">
              <span className="flex items-center gap-1"><MapPin className="h-4 w-4 text-emerald-500" /> {job.city} / {job.district}</span>
              {job.area_m2 && <span className="flex items-center gap-1"><Ruler className="h-4 w-4 text-emerald-500" /> {job.area_m2}</span>}
              {job.material_status && <span className="flex items-center gap-1"><Package className="h-4 w-4 text-emerald-500" /> {job.material_status}</span>}
              <span className="flex items-center gap-1"><Clock className="h-4 w-4 text-emerald-500" /> {timeAgo(job.created_at)}</span>
            </div>
            <p className="text-slate-700 leading-relaxed mt-5 whitespace-pre-line">{job.description}</p>
            {job.photos?.length > 1 && (
              <div className="grid grid-cols-3 gap-2 mt-5">
                {job.photos.slice(1).map((p, i) => <img key={i} src={p} alt="" className="rounded-xl h-24 w-full object-cover" />)}
              </div>
            )}
          </div>
        </div>

        {/* Offer form for usta */}
        {!isOwner && (
          <div className="bg-white rounded-2xl border border-slate-200/80 p-6">
            <h3 className="text-lg font-bold text-slate-900">Teklif Gönder</h3>
            <p className="text-sm text-slate-500 mt-1">İşveren teklifleri karşılaştırıp sizinle iletişime geçebilir.</p>
            {user && !user.phone_verified && (
              <div className="mt-3 flex items-start gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-100 rounded-xl p-3" data-testid="offer-phone-warning">
                <ShieldCheck className="h-4 w-4 mt-0.5 shrink-0" />
                <span>Teklif verebilmek için telefon numaranızı doğrulamanız gerekiyor. <button onClick={() => navigate("/hesabim?tab=phone")} className="font-semibold underline">Şimdi doğrula</button></span>
              </div>
            )}
            <div className="grid gap-3 mt-4">
              <Input type="number" step="500" min="500" max="1000000" placeholder="Teklif tutarı (TL, 500'ün katları, en fazla 1.000.000)" value={offerForm.price}
                onChange={(e) => setOfferForm({ ...offerForm, price: e.target.value })}
                onBlur={(e) => setOfferForm({ ...offerForm, price: e.target.value ? String(Math.min(1000000, Math.max(500, Math.round(Number(e.target.value) / 500) * 500))) : "" })}
                className="rounded-xl h-11" data-testid="offer-price-input" />
              <p className="text-xs text-slate-400 mt-1">1.000.000 TL üzeri girerseniz teklif <span className="font-semibold text-emerald-600">1.000.000+</span> olarak iletilir.</p>
            </div>
            <div className="flex items-center justify-between mt-3">
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <Switch checked={offerForm.materials_included} onCheckedChange={(v) => setOfferForm({ ...offerForm, materials_included: v })} data-testid="offer-materials-switch" />
                Malzeme dahil
              </label>
            </div>
            <Button onClick={submitOffer} disabled={submitting || !offerForm.price} className="w-full brand-gradient text-white rounded-xl h-11 mt-4 font-semibold" data-testid="job-offer-submit-button">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Teklif Gönder"}
            </Button>
          </div>
        )}

        {/* Offers list for owner */}
        {isOwner && (
          <div className="bg-white rounded-2xl border border-slate-200/80 p-6">
            <h3 className="text-lg font-bold text-slate-900">Gelen Teklifler ({offers.length})</h3>
            {offers.length === 0 ? (
              <p className="text-sm text-slate-500 mt-3">Henüz teklif gelmedi.</p>
            ) : (
              <div className="space-y-3 mt-4">
                {offers.map((o) => (
                  <div key={o.id} className="border border-slate-200 rounded-xl p-4" data-testid={`offer-item-${o.id}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Avatar className="h-10 w-10"><AvatarImage src={o.usta_avatar} /><AvatarFallback className="bg-emerald-100 text-emerald-700">{o.usta_name?.[0]}</AvatarFallback></Avatar>
                        <div>
                          <div className="font-bold text-slate-900 text-sm">{o.usta_name}</div>
                          <div className="flex items-center gap-1 text-xs text-slate-500"><Star className="h-3 w-3 fill-amber-400 text-amber-400" /> {o.usta_rating || "Yeni"}</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-extrabold text-emerald-600">{o.is_over ? "1.000.000+ ₺" : tl(o.price)}</div>
                        <div className="text-xs text-slate-400">{o.duration_days} gün</div>
                      </div>
                    </div>
                    <p className="text-sm text-slate-600 mt-2">{o.description}</p>
                    <div className="flex items-center gap-2 mt-3">
                      {o.status === "pending" ? (
                        <>
                          <Button onClick={() => acceptOffer(o.id)} size="sm" className="brand-gradient text-white rounded-lg" data-testid={`accept-offer-${o.id}`}>Kabul Et</Button>
                          <Button onClick={() => rejectOffer(o.id)} size="sm" variant="outline" className="rounded-lg">Reddet</Button>
                          <Button onClick={() => navigate(`/mesajlar?job=${id}&to=${o.usta_id}`)} size="sm" variant="ghost" className="rounded-lg text-emerald-600">Mesaj Gönder</Button>
                        </>
                      ) : (
                        <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${o.status === "accepted" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                          {o.status === "accepted" ? "Kabul edildi" : o.status === "rejected" ? "Reddedildi" : o.status}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {job.status === "assigned" && (
              <Button onClick={completeJob} className="w-full mt-4 rounded-xl bg-emerald-600 text-white" data-testid="complete-job-button">İşi Tamamlandı Olarak İşaretle</Button>
            )}
          </div>
        )}
      </div>

      {/* Right sidebar */}
      <div className="space-y-4">
        <div className="bg-white rounded-2xl border border-slate-200/80 p-6">
          <div className="text-xs text-slate-400">Tahmini Bütçe</div>
          <div className="text-2xl font-extrabold text-slate-900">
            {job.budget_min || job.budget_max ? `${new Intl.NumberFormat("tr-TR").format(job.budget_min || 0)} - ${tl(job.budget_max || 0)}` : "Görüşülecek"}
          </div>

          {/* Contact unlock section */}
          <div className="mt-5 pt-5 border-t border-slate-100">
            <div className="text-sm font-bold text-slate-900 mb-2">İşveren İletişim Bilgisi</div>
            {!isOwner ? (
              <ContactUnlock targetUserId={job.employer_id} jobId={id} />
            ) : (
              <div className="text-sm text-slate-400">Bu sizin ilanınız.</div>
            )}
          </div>

          {user?.role === "admin" && !isOwner && (
            <>
              <Button onClick={adminCancel} variant="outline" className="w-full rounded-xl h-10 mt-3 border-red-200 text-red-600 hover:bg-red-50" data-testid="admin-cancel-job">
                <Trash2 className="h-4 w-4 mr-1.5" /> Yönetici: İlanı Kaldır
              </Button>
              <Button onClick={openEdit} variant="outline" className="w-full rounded-xl h-10 mt-2 border-violet-200 text-violet-700 hover:bg-violet-50" data-testid="admin-edit-job">
                <Pencil className="h-4 w-4 mr-1.5" /> Yönetici: İlanı Düzenle
              </Button>
            </>
          )}

          {!isOwner && (
            <Button onClick={startChat} variant="outline" className="w-full rounded-xl h-11 mt-3 border-emerald-200 text-emerald-700" data-testid="start-chat-button">
              <Send className="h-4 w-4 mr-1.5" /> Mesaj Gönder
            </Button>
          )}
          {isOwner && (
            <div className="mt-3 pt-3 border-t border-slate-100 space-y-2">
              <div className="text-sm font-bold text-slate-900">İlan Yönetimi</div>
              {job.boost_expires_at && new Date(job.boost_expires_at) > new Date() ? (
                <>
                  <BoostCountdown expiresAt={job.boost_expires_at} className="w-full justify-center py-2" />
                  <Button onClick={() => setBoostOpen(true)} className="w-full rounded-xl h-10 bg-amber-500 hover:bg-amber-600 text-white" data-testid="job-extend-boost">
                    <Rocket className="h-4 w-4 mr-1.5" /> Süreyi Uzat
                  </Button>
                </>
              ) : (
                <Button onClick={() => setBoostOpen(true)} className="w-full rounded-xl h-10 bg-amber-500 hover:bg-amber-600 text-white" data-testid="job-boost-button">
                  <Rocket className="h-4 w-4 mr-1.5" /> İlanı Öne Çıkar
                </Button>
              )}
              <Button onClick={openEdit} variant="outline" className="w-full rounded-xl h-10 border-emerald-200 text-emerald-700" data-testid="job-edit-button">
                <Pencil className="h-4 w-4 mr-1.5" /> İlanı Düzenle
              </Button>
              <Button onClick={removeJob} variant="outline" className="w-full rounded-xl h-10 border-red-200 text-red-600 hover:bg-red-50" data-testid="job-remove-button">
                <Trash2 className="h-4 w-4 mr-1.5" /> İlanı Kaldır
              </Button>
            </div>
          )}
        </div>

        <div className="bg-white rounded-2xl border border-slate-200/80 p-5">
          <div className="flex items-center gap-2 text-emerald-700 font-semibold text-sm"><ShieldCheck className="h-4 w-4" /> Güvenli İletişim</div>
          <p className="text-xs text-slate-500 mt-2 leading-relaxed">
            Ustayla taraflar arasındaki hizmet bedelini tahsil etmez. İletişim bilgisi yalnızca ödeme yapan yetkili ustaya, yalnızca bu ilan için gösterilir.
          </p>
        </div>

        {isUsta && (
          <div className="brand-gradient text-white rounded-2xl p-5">
            <div className="font-bold">İletişim açma ücreti: {price} TL</div>
            <p className="text-sm text-emerald-50 mt-1">Cüzdanınızdan düşülür. Önce bonus bakiye kullanılır.</p>
          </div>
        )}
      </div>

      {/* Unlock confirm modal */}
      <Dialog open={unlockOpen} onOpenChange={setUnlockOpen}>
        <DialogContent className="sm:max-w-md rounded-2xl" data-testid="unlock-confirm-modal">
          <DialogHeader><DialogTitle>İletişim Bilgisini Aç</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">Bu işlem için cüzdanınızdan <span className="font-bold text-emerald-600">{tl(price)}</span> düşülecektir.</p>
            <div className="bg-slate-50 rounded-xl p-4 text-sm space-y-1">
              <div className="flex justify-between"><span className="text-slate-500">Bonus Bakiye</span><span className="font-semibold">{tl(wallet?.bonus_balance || 0)}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Gerçek Bakiye</span><span className="font-semibold">{tl(wallet?.real_balance || 0)}</span></div>
              <div className="flex justify-between border-t pt-1 mt-1"><span className="text-slate-700 font-semibold">Kullanılabilir</span><span className="font-bold text-emerald-600">{tl(wallet?.available_balance || 0)}</span></div>
            </div>
            {(wallet?.available_balance || 0) < price ? (
              <div className="text-sm text-red-500">Yetersiz bakiye. <button onClick={() => navigate("/cuzdanim")} className="underline font-semibold">Bakiye yükle</button></div>
            ) : (
              <Button onClick={doUnlock} disabled={unlocking} className="w-full brand-gradient text-white rounded-xl h-11 font-semibold" data-testid="contact-unlock-confirm-button">
                {unlocking ? <Loader2 className="h-4 w-4 animate-spin" /> : `${tl(price)} ile İletişimi Aç`}
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <AuthModal open={authOpen} onOpenChange={setAuthOpen} />
      <BoostModal open={boostOpen} onOpenChange={setBoostOpen} type="job" jobId={id} onDone={load} />

      {/* Edit job modal */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-md rounded-2xl" data-testid="job-edit-modal">
          <DialogHeader><DialogTitle>İlanı Düzenle</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input value={editForm.title} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} placeholder="Başlık" className="rounded-xl" data-testid="job-edit-title" />
            <Textarea value={editForm.description} maxLength={500} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} placeholder="Açıklama" className="rounded-xl min-h-24" data-testid="job-edit-description" />
            <Input value={editForm.area_m2} onChange={(e) => setEditForm({ ...editForm, area_m2: e.target.value })} placeholder="Ölçü / m²" className="rounded-xl" />
            <div className="grid grid-cols-2 gap-2">
              <Input type="number" value={editForm.budget_min} onChange={(e) => setEditForm({ ...editForm, budget_min: e.target.value })} placeholder="Min bütçe" className="rounded-xl" />
              <Input type="number" value={editForm.budget_max} onChange={(e) => setEditForm({ ...editForm, budget_max: e.target.value })} placeholder="Max bütçe" className="rounded-xl" />
            </div>
            <Button onClick={saveEdit} className="w-full brand-gradient text-white rounded-xl h-11 font-semibold" data-testid="job-edit-save">Değişiklikleri Kaydet</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
